import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Button,
  IconButton,
  TextField,
  Box,
  Grid,
  CircularProgress,
  Snackbar,
  Alert
} from '@mui/material';
import EditIcon      from '@mui/icons-material/Edit';
import SaveIcon      from '@mui/icons-material/Save';
import CloseIcon     from '@mui/icons-material/Close';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ErrorOutline  from '@mui/icons-material/ErrorOutline';
import Icon          from '@mui/material/Icon';

import { api } from '../../services/api';
import { isPending } from '../../services/apiUtils';
import ResearchTypeSelector from '../../components/ResearchTypeSelector';

const labelSx = { color: 'text.secondary', fontWeight: 500 };

function SurveyPromptEditor({
  userData,
  setUserData,
  onRegenerate,
  setWarningMessage,
  setOpenWarningSnackbar,
  onTopicUpdate,
  researchTypes,
  loading,
  polling,
  trimLength = 120,
  readOnly
}) {
  /* ---------------- local state ---------------- */
  const [editing,  setEditing]  = useState(false);
  const [showFull, setShowFull] = useState(false);

  const [editedPrompt, setEditedPrompt] = useState(userData.description);

  const getDefaultType = (initialValue) => {
    if (initialValue && researchTypes.some(t => t.id === initialValue)) return initialValue;
    const general = researchTypes.find(t => t.id === 'general');
    return general ? 'general' : researchTypes[0]?.id || '';
  };
  const [editedType, setEditedType] = useState(() => getDefaultType(userData.researchType));

  const [saving,    setSaving]    = useState(false);
  const [showSaved, setShowSaved] = useState(false);

  const [madeChanges, setMadeChanges] = useState(false);

  /* ------------- sync when parent changes ------------- */
  useEffect(() => {
    setEditing(false);
    setShowFull(false);
    setEditedPrompt(userData.description);
    setEditedType(getDefaultType(userData.researchType));
    setMadeChanges(false);
  }, [userData.researchID]);

    useEffect(() => {
    if (loading || polling || readOnly) {
      setEditing(false);
    }
  }, [loading, polling, readOnly]);

  useEffect(() => {
    if (editing) {
      if (editedPrompt !== userData.description || editedType !== userData.researchType) {
        setMadeChanges(true);
      } else {
        setMadeChanges(false);
      }
    }
  }, [editedPrompt, editedType]);


  /* ---------------- handlers ---------------- */
  const doSave = async () => {
    if (readOnly) return;
    if (polling || isPending(userData, setWarningMessage, setOpenWarningSnackbar)) return;

    const trimmedOriginal = userData.description.trim();
    const trimmedNew      = editedPrompt.trim();

    const promptChanged = trimmedOriginal !== trimmedNew;
    const typeChanged   = editedType !== userData.researchType;

    if (!promptChanged && !typeChanged) {
      setEditing(false);
      return;
    }

    try {
      setSaving(true);
      const result = await api.updateResearch(
        userData.userID,
        userData.researchID,
        {
          ...(promptChanged ? { description: trimmedNew } : {}),
          ...(typeChanged   ? { researchType: editedType } : {})
        }
      );
      const newData = { ...userData, ...result };
      setUserData(newData);
      onTopicUpdate(newData);
      setShowSaved(true);
      setEditing(false);
    } catch (err) {
      console.error('Failed updating research:', err);
    } finally {
      setSaving(false);
    }
  };

  const doCancel = () => {
    setEditedPrompt(userData.description);
    setEditedType(getDefaultType(userData.researchType));
    setEditing(false);
  };

  const handleRegenerate = () => {
    if (readOnly) return;
    onRegenerate();
    setUserData(prev => ({ ...prev, surveyStatus: undefined }));
  };

  /* save first, then regenerate */
  const handleSaveAndRegenerate = async () => {
  if (readOnly) return;
  await doSave(); // wait until the settings are really persisted
  handleRegenerate(); // then kick-off the regeneration
  };

  /* ---------------- derived ---------------- */
  const selectedType = researchTypes.find(t => t.id === editedType) || researchTypes[0];
  const showPromptChanged = userData?.surveyStatus === 'PROMPT_CHANGED';
  const displayPrompt = showFull
    ? userData.description
    : userData.description.length > trimLength
      ? userData.description.slice(0, trimLength) + '…'
      : userData.description;

  /* ---------------- UI ---------------- */
  return (
    <>
      <Paper sx={{ p: 3, mt: 3, position: 'relative' }}>
        {/* card title */}
        <Typography variant="h6" sx={{ mb: 3 }}>
          Research &amp; Survey Settings
        </Typography>

        {/* top-right actions */}
        {!polling && !readOnly && (

        <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
          {!editing ? (
            <IconButton size="small" onClick={() => setEditing(true)}>
              <EditIcon fontSize="small" />
            </IconButton>
          ) : (
            <>
              <Button
                size="small"
                variant="contained"
                startIcon={saving ? null : <SaveIcon />}
                onClick={doSave}
                disabled={saving || !madeChanges}
                sx={{ mr: 1 }}
              >
                {saving ? <CircularProgress size={16} /> : 'Save'}
              </Button>
              <IconButton size="small" onClick={doCancel} disabled={saving}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </>
          )}
        </Box>          
        )}


        {/* common grid – reused in both states */}
        <Grid container spacing={2}>
          {/* ---- Research Type row ---- */}
          <Grid item xs={12} sm={3}>
            <Typography variant="subtitle2" sx={labelSx}>
              Research Type
            </Typography>
          </Grid>

          <Grid item xs={12} sm={9}>
            {!editing ? (
              <Box display="flex" alignItems="center">
                <Icon color="primary" sx={{ transform: 'scale(1.8)', mr: 2.5 }}>{selectedType.icon}</Icon>
                <Typography variant="body1" fontWeight={600}>
                  {selectedType.name}
                </Typography>
              </Box>
            ) : (
              <ResearchTypeSelector
                types={researchTypes}
                value={editedType}
                onChange={setEditedType}
                embed
              />
            )}
          </Grid>

          {/* ---- Prompt row ---- */}
          <Grid item xs={12} sm={3}>
            <Typography variant="subtitle2" sx={labelSx}>
              Research Prompt
            </Typography>
          </Grid>

          <Grid item xs={12} sm={9}>
            {!editing ? (
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                {displayPrompt}
                {userData.description.length > trimLength && (
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => setShowFull(p => !p)}
                    sx={{ ml: 1, p: 0, textTransform: 'none', minWidth: 0 }}
                  >
                    {showFull ? 'Show less' : 'Show more'}
                  </Button>
                )}
              </Typography>
            ) : (
              <TextField
                fullWidth
                multiline
                minRows={5}
                value={editedPrompt}
                onChange={e => setEditedPrompt(e.target.value)}
              />
            )}
          </Grid>
        </Grid>
      </Paper>

      {/* ----------- regenerate notice & button ----------- */}
      <Box
        sx={{
          mt: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}
      >
        {showPromptChanged && !readOnly && (
          <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', color: 'warning.main' }}>
            <ErrorOutline sx={{ fontSize: 18, mr: 0.5 }} />
            <Typography variant="body2">
              Settings updated — Regenerate survey to apply changes
            </Typography>
          </Box>
        )}

{!polling && !loading && !readOnly && (
  <Button
    variant="contained"
    startIcon={
      loading || saving
        ? null
        : editing && madeChanges
          ? <SaveIcon />
          : <RestartAltIcon />
    }
    onClick={editing && madeChanges ? handleSaveAndRegenerate : handleRegenerate}
    disabled={saving}
    sx={{
      mr: 1,
      bgcolor: showPromptChanged ? 'warning.main' : 'primary.main',
      '&:hover': {
        bgcolor: showPromptChanged ? 'warning.dark' : 'primary.dark',
      },
    }}
  >

    {editing && madeChanges
        ? 'Save & Regenerate'
        : 'Regenerate Survey'}
  </Button>
)}

      </Box>


    </>
  );
}

export default SurveyPromptEditor;
